import { useCallback, useEffect, useState } from 'react';
import {
    Button,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Autocomplete,
} from '@mui/material';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import { CookService } from '../../services/CookServices';
import EventBus from '../../EventBus';
import { EventBusEventsEnum } from '../../enums/EventBusEventsEnum';
import { CustomSnackbarTypes } from '../Common/CustomSnackBar';

interface InputProps {
    cookId: number;
}

const cookService = new CookService();

const CookRecommendedDishes = ({ cookId }: InputProps) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const [category, setCategory] = useState<{ id: number; name: string }[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<number>();

    const [dishes, setDishes] = useState<{ id: number; name: string }[]>([]);
    const [mealType, setMealType] = useState<string>('ALL');
    const [filteredDishes, setFilteredDishes] = useState<any>([]);
    const [selectedDish, setSelectedDish] = useState<number>();

    const refreshData = useCallback(() => {
        if (!cookId) return;
        setLoading(true);
        cookService
            .getAllRecommendedDish(cookId)
            .then((res: any) => {
                setData(Array.isArray(res) ? res : []);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [cookId]);

    const handleSubmit = () => {
        if (!selectedCategory) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: 'Select a Category', type: CustomSnackbarTypes.WARNING });
            return;
        }
        if (!selectedDish) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: 'Select a Dish', type: CustomSnackbarTypes.WARNING });
            return;
        }
        cookService.addRecommendedDish(cookId, selectedDish).then(() => {
            refreshData();
            setOpen(false);
            setSelectedDish(undefined);
            setSelectedCategory(undefined);
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: 'Added Successfully', type: CustomSnackbarTypes.SUCCESS });
        });
    };

    function changeFilteredDishes(type: string) {
        setMealType(type);
        setFilteredDishes(type === 'ALL' ? dishes : dishes.filter((dish: any) => dish?.type === type));
    }

    const handleDelete = (id: number) => {
        EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
            title: 'Are you sure?',
            message: 'Please confirm to remove this dish',
            successLabel: 'Confirm',
            failureLabel: 'Cancel',
            onClose: (confirmed: boolean) => {
                if (confirmed) {
                    cookService.removeRecommendedDish(cookId, id).then(() => {
                        refreshData();
                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: 'Removed Successfully', type: CustomSnackbarTypes.SUCCESS });
                    });
                }
            },
        });
    };

    useEffect(() => { refreshData(); }, [refreshData]);

    useEffect(() => {
        if (!selectedCategory) return;
        cookService.getAllCuisineCategoryDishes(selectedCategory).then((res: { id: number; name: string }[]) => {
            setDishes(res);
            setFilteredDishes(res);
            setSelectedDish(undefined);
        });
    }, [selectedCategory]);

    useEffect(() => {
        cookService.getAllCuisineCategories().then((res: { id: number; name: string }[]) => {
            setCategory(res);
        });
    }, []);

    const columns: CommonListColumn[] = [
        { label: 'Id', field: 'id' },
        { label: 'Name', render: (row) => row?.name ?? '-' },
        { label: 'Category', render: (row) => row?.category?.name ?? '-' },
        { label: 'Meal Type', render: (row) => row?.mealType ?? '-' },
        { label: 'Type', render: (row) => row?.type ?? '-' },
        {
            label: '',
            stopRowClick: true,
            render: (row) => (
                <Button onClick={() => handleDelete(row?.id)} variant="contained" color="error" size="small">
                    Remove
                </Button>
            ),
        },
    ];

    return (
        <div>
            <Box display="flex" flexDirection="row-reverse" my={2}>
                <Button size="small" variant="contained" onClick={() => setOpen(true)}>
                    Add Dish
                </Button>
            </Box>
            <CommonListTable
                columns={columns}
                data={data}
                loading={loading}
                emptyText="No Recommended Dishes"
            />
            {open && (
                <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md">
                    <DialogTitle>Add Dish</DialogTitle>
                    <DialogContent sx={{ minWidth: 350 }}>
                        <Box display="flex" flexDirection="column">
                            <TextField
                                id="select-cuisine-select"
                                select
                                label="Select Cuisine"
                                fullWidth
                                value={selectedCategory}
                                onChange={(e: any) => setSelectedCategory(e.target.value)}
                                SelectProps={{ native: false }}
                            >
                                {category.map((option) => (
                                    <MenuItem key={option?.id} value={option?.id}>{option.name}</MenuItem>
                                ))}
                            </TextField>
                            {selectedCategory && dishes && dishes.length > 0 && (
                                <TextField
                                    id="select-mealtype-select"
                                    select
                                    label="Select Meal Type"
                                    fullWidth
                                    value={mealType}
                                    onChange={(e: any) => changeFilteredDishes(e.target.value)}
                                    SelectProps={{ native: false }}
                                >
                                    <MenuItem value="ALL">ALL</MenuItem>
                                    <MenuItem value="VEG">VEG</MenuItem>
                                    <MenuItem value="NON_VEG">NON_VEG</MenuItem>
                                </TextField>
                            )}
                            {selectedCategory && (
                                <Autocomplete
                                    id="select-dish-select"
                                    options={filteredDishes
                                        ?.sort((a: any, b: any) => a?.name?.localeCompare(b?.name))
                                        ?.map((each: { id: number; name: string }) => ({ label: each?.name, value: each?.id }))}
                                    onChange={(_, val: any) => setSelectedDish(val?.value)}
                                    renderInput={(params) => <TextField {...params} label="Select Dish" />}
                                />
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button size="small" variant="outlined" onClick={() => setOpen(false)}>close</Button>
                        <Button size="small" variant="contained" color="error" onClick={handleSubmit}>Submit</Button>
                    </DialogActions>
                </Dialog>
            )}
        </div>
    );
};

export default CookRecommendedDishes;
